import React, { useState, useRef } from 'react'
import { 
  Button, 
  ClickAwayListener, 
  Grow, 
  MenuItem, 
  MenuList, 
  Paper, 
  Popper 
} from '@mui/material'
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { useCustomNavigation } from '../../../hooks/useCustomNavigate'
import { NavigationEnum } from '../../../utils/constants'
import styles from './NavButton.module.scss'

interface NavButtonProps {
  title: string
  value: number
  listOfItems?: Array<{
    name: string
    value: string | number
    to: NavigationEnum
    params?: string // Make sure params is included in the interface
  }>
  selected: boolean
  hovered: boolean
  buttonOnMouseEnter: (value: number) => void
  to: NavigationEnum
  params?: string
  setSelectedButton: (value: number) => void
}

export const NavButton: React.FC<NavButtonProps> = ({
  title,
  value,
  listOfItems,
  selected,
  hovered,
  buttonOnMouseEnter,
  to,
  params,
  setSelectedButton
}) => {
  const { t } = useTranslation()
  const customNavigate = useCustomNavigation()
  const [open, setOpen] = useState(false)
  const anchorRef = useRef<HTMLButtonElement>(null)

  const hasDropdown = listOfItems && listOfItems.length > 0

  const handleToggle = () => {
    if (hasDropdown) {
      setOpen((prevOpen) => !prevOpen)
    } else {
      handleNavigation()
    }
  }

  const handleClose = (event: Event | React.SyntheticEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return
    }
    setOpen(false)
  }

  const handleNavigation = () => {
    if (to !== NavigationEnum.NOWHERE) {
      if (params) {
        customNavigate(to, params)
      } else {
        customNavigate(to)
      }
      setSelectedButton(value)
    }
    setOpen(false)
  }

  const handleSubItemClick = (subItem: any) => {
    if (subItem.to !== NavigationEnum.NOWHERE) {
      // Handle parameters properly for sub-items
      if (subItem.params) {
        // Create filters object based on your initialFiltersConst and the category parameter
        const filters = {
          orientation: 'all',
          items: 100,
          category: subItem.params.toUpperCase() // Convert 'eps' to 'EPS'
        }
        
        // Encode the filters object as JSON and then URL encode it
        const encodedFilters = encodeURIComponent(JSON.stringify(filters))
        const urlWithFilters = `${subItem.to}?filters=${encodedFilters}`
        
        customNavigate(urlWithFilters)
      } else {
        customNavigate(subItem.to)
      }
      setSelectedButton(value)
    }
    setOpen(false)
  }

  const handleListKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Tab') {
      event.preventDefault()
      setOpen(false)
    } else if (event.key === 'Escape') {
      setOpen(false)
    }
  }

  return (
    <div className={styles.navButtonContainer}>
      <Button
        ref={anchorRef}
        className={`${styles.navButton} ${selected ? styles.navButtonActive : ''} ${hovered ? styles.navButtonHovered : ''}`}
        onClick={handleToggle}
        onMouseEnter={() => buttonOnMouseEnter(value)}
        onMouseLeave={() => buttonOnMouseEnter(0)}
        endIcon={hasDropdown ? (open ? <KeyboardArrowUp /> : <KeyboardArrowDown />) : null}
        aria-controls={open ? 'composition-menu' : undefined}
        aria-expanded={open ? 'true' : undefined}
        aria-haspopup={hasDropdown ? 'true' : 'false'}
      >
        {title}
      </Button>
      
      {hasDropdown && (
        <Popper
          open={open}
          anchorEl={anchorRef.current}
          role={undefined}
          placement="bottom-start"
          transition
          className={styles.dropdownPopper}
          style={{ zIndex: 9999 }}
        >
          {({ TransitionProps, placement }) => (
            <Grow
              {...TransitionProps}
              style={{
                transformOrigin: placement === 'bottom-start' ? 'left top' : 'left bottom',
              }}
            >
              <Paper className={styles.dropdownPaper}>
                <ClickAwayListener onClickAway={handleClose}>
                  <MenuList
                    autoFocusItem={open}
                    id="composition-menu"
                    aria-labelledby="composition-button"
                    onKeyDown={handleListKeyDown}
                    className={styles.menuList}
                  >
                    {listOfItems.map((item) => (
                      <MenuItem
                        key={item.value}
                        onClick={() => handleSubItemClick(item)}
                        className={styles.menuItem}
                      >
                        {item.name}
                      </MenuItem>
                    ))}
                  </MenuList>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
      )}
    </div>
  )
}